import React, { useState, useEffect } from "react";
import { Box, Typography, TextField, Grid } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { grey } from "@mui/material/colors";
import { defaultStyle } from "components/Modals";

interface EditCodeModalProps {
  currentCode: string;
  setOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
  onSave: (newCode: string) => void;
}

const EditCodeModal: React.FC<EditCodeModalProps> = ({
  currentCode,
  setOpenModal,
  onSave,
}) => {
  const [loading, setLoading] = useState(false);
  const [codeId, setCodeId] = useState("");
  const [codeWord, setCodeWord] = useState("");

  useEffect(() => {
    if (currentCode) {
      const parts = currentCode.split("-");
      if (parts.length >= 2) {
        setCodeWord(parts[0]);
        setCodeId(parts.slice(1).join("-"));
      } else {
        setCodeWord(currentCode);
        setCodeId("");
      }
    }
  }, [currentCode]);

  const formatCodeWord = (word: string): string => {
    const firstWord = word.trim().split(/\s+/, 2)[0] || "";

    const normalized = firstWord
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/ß/g, "ss")
      .replace(/[^A-Za-z]/g, "")
      .toUpperCase();

    return normalized || "PARK";
  };

  const handleSubmit = () => {
    setLoading(true);
    const formattedCodeWord = formatCodeWord(codeWord);
    const newCode = formattedCodeWord
      ? `${formattedCodeWord}-${codeId}`
      : codeId;
    onSave(newCode);
    setLoading(false);
    setOpenModal(false);
  };

  return (
    <Box sx={defaultStyle}>
      <Typography id="modal-modal-title" variant="h5" component="h5">
        Edit production park code
      </Typography>
      <Grid container spacing={2} sx={{ mb: "2rem", mt: "0.6rem" }}>
        <Grid item xs={12} sm={8}>
          <TextField
            fullWidth
            label="Name"
            size="small"
            value={codeWord}
            onChange={(e) => setCodeWord(e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            label="ID"
            size="small"
            value={codeId}
            disabled
          />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="body2" sx={{ color: grey[600] }}>
            New production park code:{" "}
            <strong>
              {codeWord ? `${formatCodeWord(codeWord)}-${codeId}` : codeId}
            </strong>
          </Typography>
        </Grid>
        <Grid item xs={12} sx={{ mt: "1rem" }}>
          <LoadingButton
            variant="contained"
            loading={loading}
            loadingIndicator="Changing..."
            onClick={handleSubmit}
            disabled={loading}
            sx={{ mr: 1 }}
          >
            Change
          </LoadingButton>
          <LoadingButton
            variant="outlined"
            onClick={() => setOpenModal(false)}
            disabled={loading}
          >
            Cancel
          </LoadingButton>
        </Grid>
      </Grid>
    </Box>
  );
};

export default EditCodeModal;
